import {useEffect, useState} from "react";

/** What {@link useNudgePopover} exposes to the view: whether the popover shows, plus a dismiss action. */
export interface NudgePopoverState {
	/** True when the popover should be visible (condition active AND not currently dismissed). */
	open: boolean;
	/** Hide the popover while the condition stays active. */
	dismiss: () => void;
}

/**
 * Dismiss-on-condition popover state for the recipient-cap upgrade nudge (ui#211).
 *
 * Given whether the nudge condition is currently `active` (the recipient cap is hit or a recipient
 * error is present), returns whether the popover should be `open` and a `dismiss` action. Dismissing
 * hides the popover while the condition stays true; as soon as the condition clears the dismissal is
 * reset, so the popover re-surfaces the next time the user hits the cap again.
 */
export function useNudgePopover(active: boolean): NudgePopoverState {
	const [dismissed, setDismissed] = useState(false);

	// Reset the dismissal whenever the condition clears, so the next activation shows the popover again.
	useEffect(() => {
		if (!active) setDismissed(false);
	}, [active]);

	return {
		open: active && !dismissed,
		dismiss: () => setDismissed(true),
	};
}
